// Importing necessary libraries
var R = require('ramda');

var _require = require('./data-spec.js'),
    dataSpec = _require.dataSpec;

var _require2 = require('./widget-config.js'),
    widgetConfig = _require2.widgetConfig;

var _require3 = require('./heat-stress.js'),
    HST = _require3.HST;

var _require4 = require('./utils.js'),
    toFixed = _require4.toFixed,
    toInt = _require4.toInt;

var ambient = dataSpec; // heat stress configuration 

ambient.HST = HST; // Function to extract private parameters from data specification

ambient.PRIVATE_PARAMS = R.pipe(R.toPairs, R.filter(function (arr) {
  // Filtering parameters marked as private
  return arr[1].private;
}), R.map(function (arr) {
  // Extracting parameter names
  return arr[0];
}), R.concat(['indoor', 'battery']) // Adding additional parameters
)(ambient.DATA_SPEC); // Function to check if a value is something (not null, undefined, or NaN)

ambient.isSomething = function (v) {
  return v || v === 0;
}; // Function to convert wind direction value to label


ambient.windDirLabel = function (val) {
  // Mapping wind direction value to label
  if (val < 11.25) {
    val = 'N';
  } else if (val < 33.75) {
    val = 'NNE';
  } else if (val < 56.25) {
    val = 'NE';
  } else if (val < 78.75) {
    val = 'ENE';
  } else if (val < 101.25) {
    val = 'E';
  } else if (val < 123.75) {
    val = 'ESE';
  } else if (val < 146.25) {
    val = 'SE';
  } else if (val < 168.75) {
    val = 'SSE';
  } else if (val < 191.25) {
    val = 'S';
  } else if (val < 213.75) {
    val = 'SSW';
  } else if (val < 236.25) {
    val = 'SW';
  } else if (val < 258.75) {
    val = 'WSW';
  } else if (val < 281.25) {
    val = 'W';
  } else if (val < 303.75) {
    val = 'WNW';
  } else if (val < 326.25) {
    val = 'NW';
  } else if (val < 348.75) {
    val = 'NNW';
  } else {
    val = 'N';
  }

  return val;
}; // Rounds down a date to the nearest minute


ambient.roundDownMins = R.curry(function roundDownMins(mins, d) {
  var amt = 1000 * 60 * mins;
  return Math.floor(d / amt) * amt;
}); // Rounds down the 'dateutc' property of each object in an array to the nearest minute

ambient.roundDataDateutc = R.curry(function roundDataDateutc(mins) {
  return R.map(R.over(R.lensProp('dateutc'), ambient.roundDownMins(mins)));
}); // Formats a property value based on its type and conversion rules

ambient.formatProp = function formatProp(prop, value) {
  // Handling special cases like 'dateutc' and 'lightning_distance'
  // Skipping other properties if not defined in DATA_SPEC
  // Applying conversions based on property type
  if (prop === 'dateutc') {
    var d = Date.parse(value);

    if (isNaN(d)) {
      d = parseInt(value, 10);
    }

    if (isNaN(d)) {
      d = Date.now();
    }

    return ambient.roundDownMins(1, d);
  }

  if (!ambient.DATA_SPEC[prop]) return value;

  if (prop === 'lightning_distance') {
    var v = parseFloat(value, 10);

    if (isNaN(v)) {
      return 0;
    }

    return parseFloat((v * 0.62137).toFixed(2), 10);
  }

  var type = ambient.DATA_SPEC[prop].type;
  var ret = value;

  switch (type) {
    case 'Int':
      ret = parseInt(value, 10);
      break;

    case 'Bool':
      ret = parseInt(value, 10);
      break;

    case 'Float':
      ret = parseFloat(value, 10);
      break;

    case 'Date':
      ret = parseInt(value, 10) * 1000;
      break;
  }

  return ret;
}; // Formats properties of an object based on formatProp function


ambient.formatProps = R.mapObjIndexed(R.flip(ambient.formatProp)); // Returns display properties based on label availability from DATA_SPEC

ambient.getDisplayProps = function getDisplayProps() {
  return R.pipe(R.toPairs, R.filter(function (arr) {
    return R.prop('label', arr[1]);
  }), R.fromPairs)(ambient.DATA_SPEC);
}; // Converts Fahrenheit to Celsius


ambient.toCel = function toCel(f) {
  return 5 / 9 * (f - 32);
}; // Converts Celsius to Fahrenheit


ambient.toFahr = function toFahr(c) {
  return c * (9 / 5) + 32;
}; // Returns device label based on device info


ambient.getDeviceLabel = function getDeviceLabel(dev) {
  // Building device label from name and location information
  var label = '';

  if (dev && dev.info) {
    if (dev.info.name) {
      label = dev.info.name;
    }

    if (dev.info.coords && dev.info.coords.location) {
      label += ', ' + dev.info.coords.location;
    }

    if (dev.info.location) {
      label += ' - ' + dev.info.location;
    }
  }

  return label;
}; // Object containing conversion functions for various properties


ambient.conversions = {
  // Conversion functions for various properties
  // Each property has an array of conversion functions
  tempf: [R.pipe(R.identity, toFixed(1)), R.pipe(ambient.toCel, toFixed(1))],
  baromrelin: [R.pipe(R.identity, toFixed(2)), R.pipe(R.divide(R.__, 0.0393700791974), toFixed(1)), R.pipe(R.multiply(33.86389), toFixed(1))],
  windspeedmph: [R.pipe(R.identity, toFixed(1)), // ft/sec
  R.pipe(R.multiply(1.4666667136), toFixed(1)), // m/sec
  R.pipe(R.multiply(0.44704), toFixed(1)), // km/hr
  R.pipe(R.multiply(1.609344), toFixed(1)), // knots
  R.pipe(R.multiply(0.86897624190816), toFixed(1))],
  hourlyrainin: [R.pipe(R.identity, toFixed(2)), R.pipe(R.multiply(25.4), toFixed(1))],
  dailyrainin: [R.pipe(R.identity, toFixed(2)), R.pipe(R.multiply(25.4), toFixed(1))],
  solarradiation: [R.pipe(R.identity, toFixed(1)), R.pipe(R.multiply(126.7), Math.round)],
  soilhum1: [R.identity, function (v) {
    return Math.floor(v / 99 * 15 + 1);
  }],
  lightning_distance: [// miles
  toInt, // km
  R.pipe(R.multiply(1.609344), toFixed(1))],
  soiltens1: [R.pipe(R.identity, toFixed(0)), R.pipe(R.identity, toFixed(0))],
  leafwetsum: [// conversions
  // minutes
  R.pipe(R.identity, toFixed(0)), // hours
  R.pipe(R.divide(R.__, 60), toFixed(1))],
  etos: [//inches/day
  R.pipe(R.identity, toFixed(2)), //mm/day
  R.pipe(R.multiply(25.4), toFixed(1))],
  ft3s: [R.pipe(R.identity, toFixed(1))],
  ft: [R.pipe(R.identity, toFixed(2))],
  pH: [R.pipe(R.identity, toFixed(2))],
  mgl: [R.pipe(R.identity, toFixed(2))],
  altitudeft: [R.identity, R.pipe(R.multiply(0.3048), toFixed(1))],
  airdensitylbft3: [R.identity, R.pipe(R.multiply(16.0185), toFixed(1))],
  evapratelbft3hr: [R.identity, R.pipe(R.multiply(16.0185), toFixed(1))],
  airflowcfm: [R.identity, R.pipe(R.multiply(0.028), toFixed(1))],
  humidityratiogplb: [R.identity, R.pipe(R.multiply(0.142857), toFixed(1))]
}; // Object containing conversion functions for converting values from master unit to user unit

var conversionsInverse = {
  // Conversion functions for various properties (inverse)
  tempf: [R.identity, ambient.toFahr],
  baromrelin: [R.identity, R.pipe(R.multiply(0.0393700791974)), R.pipe(R.divide(R.__, 33.86389))],
  windspeedmph: [R.pipe(R.identity), // ft/sec
  R.pipe(R.divide(R.__, 1.4666667136)), // m/sec
  R.pipe(R.divide(R.__, 0.44704)), // km/hr
  R.pipe(R.divide(R.__, 1.609344)), // knots
  R.pipe(R.divide(R.__, 0.86897624190816))],
  hourlyrainin: [R.pipe(R.identity), R.pipe(R.divide(R.__, 25.4))],
  dailyrainin: [R.pipe(R.identity), R.pipe(R.divide(R.__, 25.4))],
  solarradiation: [R.pipe(R.identity), R.pipe(R.divide(R.__, 126.7))],
  soilhum1: [R.identity, function (v) {
    return Math.floor((v - 1) / 15 * 99);
  }],
  lightning_distance: [// miles
  toInt, // km
  R.divide(R.__, 1.609344)],
  soiltens1: [R.pipe(R.identity, toFixed(1)), R.pipe(R.identity, toFixed(1))],
  leafwetsum: [// inverse
  // minutes
  R.pipe(R.multiply(60), toFixed(0)), // hours
  R.pipe(R.identity, toFixed(1))],
  etos: [// inches/day
  R.pipe(R.identity), // mm/day
  R.pipe(R.divide(R.__, 25.4))],
  altitudeft: [R.identity, R.pipe(R.divide(R.__, 0.3048), toFixed(1))],
  airdensitylbft3: [R.identity, R.pipe(R.divide(R.__, 16.0185), toFixed(1))],
  evapratelbft3hr: [R.identity, R.pipe(R.divide(R.__, 16.0185), toFixed(1))],
  airflowcfm: [R.identity, R.pipe(R.divide(R.__, 0.028), toFixed(1))],
  humidityratiogplb: [R.identity, R.pipe(R.divide(R.__, 0.142857), toFixed(1))]
}; // Function to append path to an array and then get the value at that path

function appendToPath(pth) {
  return R.pipe(R.flip(R.append)(pth), R.path);
} // Function to retrieve user settings from a given path


ambient.getUserSetting = appendToPath(['settings']); // Function to convert a value from master unit to user unit

ambient.convertUnit = function convertUnit(key, i, val) {
  if (ambient.isSomething(val)) {
    return ambient.conversions[key][i](val);
  }
}; // Function to convert a value from user unit to master unit (inverse conversion)


ambient.convertUnitInverse = function convertUnitInverse(key, i, val) {
  // Implementation to convert value based on key and index (inverse conversion)
  if (ambient.isSomething(val)) {
    return conversionsInverse[key][i](val);
  }
}; // Function to retrieve the master unit parameter for a given property


ambient.getMasterUnitParam = function getMasterUnitParam(param) {
  // Implementation to determine master unit parameter based on property
  var arrOrStr = R.path([param, 'units'], ambient.DATA_SPEC);

  if (arrOrStr) {
    return Array.isArray(arrOrStr) ? param : arrOrStr;
  } else if (R.includes(param, ['dewPoint', 'feelsLike'])) {
    return 'tempf';
  }

  return false;
}; // Function to retrieve the index of user unit setting for a given property


ambient.getUserUnitI = R.curry(function getUserUnitI(key, user) {
  // Implementation to retrieve index of user unit setting for a property
  var theKey = key;

  if (key === 'dailyrainin') {
    theKey = 'hourlyrainin';
  }

  var masterUnitParam = ambient.getMasterUnitParam(theKey);

  if (masterUnitParam === 'lightning_distance') {
    masterUnitParam = 'metric';
  }

  var userSetting = ambient.getUserSetting(masterUnitParam)(user) || {};
  return ambient.isSomething(userSetting.unit) ? userSetting.unit : 0;
}); // Function to retrieve unit array for a given property

ambient.getUnitArr = function getUnitArr(param) {
  // Implementation to retrieve unit array for a property
  var arr = R.path([param, 'units'], ambient.DATA_SPEC);

  if (arr) {
    return ambient.isArrayLike(arr) ? arr : ambient.getUnitArr(arr);
  }

  return false;
}; // Function to convert value from master unit to user unit for a given property


ambient.convertUnitForUser = R.curry(function convertUnitForUser(user, param, val) {
  // Implementation to convert value from master unit to user unit for a property
  var unitParam = ambient.getMasterUnitParam(param);
  var userUnitI = ambient.getUserUnitI(param, user);

  if (unitParam) {
    return parseFloat(ambient.convertUnit(unitParam, userUnitI, val));
  }

  return val;
}); // Function to retrieve suffix for a given property type

ambient.getSuff = function getSuff(type) {
  // Implementation to retrieve suffix for a property type
  if (ambient.DATA_SPEC[type] && ambient.DATA_SPEC[type].suff) {
    return ambient.DATA_SPEC[type].suff;
  } else if (type === 'feelsLike') {
    return '°';
  }

  return '';
}; // Function to retrieve suffix for a given property type based on user settings


ambient.getSuffForUser = R.curry(function getSuffForUser(user, param) {
  // Implementation to retrieve suffix for a property based on user settings
  var unitParam = ambient.getMasterUnitParam(param);
  var userUnitI = ambient.getUserUnitI(param, user);
  var origSuff = ambient.getSuff(unitParam || param);

  if (userUnitI > 0) {
    return origSuff.replace(ambient.DATA_SPEC[unitParam].units[0], ambient.DATA_SPEC[unitParam].units[userUnitI]);
  }

  return origSuff;
}); // Function to retrieve spoken suffix for a given property type based on user settings

ambient.getSpokenSuffForUser = R.curry(function getSpokenSuffForUser(user, param) {
  // Implementation to retrieve spoken suffix for a property based on user settings
  if (/humidity/.test(param)) {
    return 'percent';
  }

  if (/^pm25/.test(param)) {
    return 'micrograms per cubic meter';
  }

  var unitParam = ambient.getMasterUnitParam(param);
  var userUnitI = ambient.getUserUnitI(param, user);
  if (unitParam) return ambient.DATA_SPEC[unitParam].spokenUnits[userUnitI];
  return '';
});
ambient.WIDGET_CONFIG = widgetConfig; // a simple widget just displays the single parameter

ambient.simpleWidgets = R.pipe(R.toPairs, R.map(function (arr) {
  if (arr[1].simpleWidget) {
    return arr[0];
  }

  return null;
}), R.filter(R.identity))(ambient.DATA_SPEC);
ambient.simpleWidgets.forEach(function (param) {
  ambient.WIDGET_CONFIG[param] = {
    title: ambient.DATA_SPEC[param].label,
    priority: 12
  };
}); // simple graphs show a generic graph of the parameter

ambient.simpleGraphs = R.pipe(R.toPairs, R.map(function (arr) {
  if (arr[1].simpleGraph) {
    return arr[0];
  }

  return null;
}), R.filter(R.identity))(ambient.DATA_SPEC); // Add soiltemp share configuration

R.range(1, 11).forEach(function (i) {
  ambient.WIDGET_CONFIG["temp".concat(i, "f")].share = {
    paths: [['currentDevice', 'lastData', "temp".concat(i, "f")], ['user', 'info', 'settings', 'tempf'], ['currentDevice', 'lastData', "humidity".concat(i)], ['sensor', R.always(i)]],
    social: true,
    component: 'IndoorWidget'
  };
  ambient.WIDGET_CONFIG['soiltemp' + i].share = {
    social: true,
    paths: [['currentDevice', 'lastData', 'soiltemp' + i], ['user', 'info', 'settings', 'soilhum1'], ['currentDevice', 'lastData', 'soilhum' + i], ['sensor', R.always(i)]],
    component: 'SoilWidget'
  };
}); // Curried function to check if data has specific paths

var dataHas = R.curry(function (data, paths) {
  return R.pipe(R.groupWith(R.F), R.all(function (pth) {
    return R.path(pth, data) || R.path(pth, data) === 0;
  }))(Array.isArray(paths) ? paths : [paths]);
});
var nonWeatherStationTypes = ['5000', '5100', '5200', '5400', '5500', '5700', 'Kestrel DROP 1', 'Kestrel DROP 2', 'Kestrel D2 AG', 'Kestrel DROP 3', 'Kestrel D3 FIRE', '3550AG', '3550FW', 'usgs-'];
ambient.deviceHasStationType = R.curry(function (stationTypes, theDevice) {
  var stationType = R.path(['lastData', 'stationtype'], theDevice);
  return stationType && stationTypes.some(function (st) {
    return stationType.includes(st);
  });
});
ambient.deviceIsAKestrel = ambient.deviceHasStationType(nonWeatherStationTypes); // Function to get widget title

var getWidgetTitle = function getWidgetTitle(device, type) {
  var val; // use an alternate widget

  if (ambient.deviceIsAKestrel(device) && type === 'temp') {
    val = ambient.WIDGET_CONFIG.tempkestrel.title; // use the same widgets, but alter it's title
  } else if (ambient.deviceIsAKestrel(device) && type === 'humidity') {
    val = 'Relative Humidity';
  } else {
    val = ambient.WIDGET_CONFIG[type] && ambient.WIDGET_CONFIG[type].title ? ambient.WIDGET_CONFIG[type].title : type.replace(/(\d+)/, ' $1');
  }

  var setting = R.path(['settings', type], device);

  if (setting) {
    val = R.path(['title'], setting) || val;
  }

  return val;
};

ambient.getWidgetTitle = getWidgetTitle; // Function to customize parameter labels

ambient.customParamLabel = function (prop, theDevice) {
  // Implementation for customizing parameter labels
  if (/temp\d/.test(prop)) {
    return getWidgetTitle(theDevice, prop) + ' Temperature';
  } else if (/pm25_in/.test(prop)) {
    return getWidgetTitle(theDevice, 'airin') + (/_24/.test(prop) ? ' 24 Hour Average' : '');
  } else if (/pm25/.test(prop)) {
    return getWidgetTitle(theDevice, 'air') + (/_24/.test(prop) ? ' 24 Hour Average' : '');
  } else if (prop === 'tempf') {
    return getWidgetTitle(theDevice, 'temp') + ' Temperature';
  } else if (prop === 'battout') {
    return getWidgetTitle(theDevice, 'temp') + ' Battery';
  } else if (prop === 'batt_25') {
    return getWidgetTitle(theDevice, 'air') + ' Battery';
  } else if (prop === 'battin') {
    return getWidgetTitle(theDevice, 'indoor') + ' Battery';
  } else if (prop === 'tempinf') {
    return getWidgetTitle(theDevice, 'indoor') + ' Temperature';
  } else if (prop === 'humidity') {
    return getWidgetTitle(theDevice, 'humidity');
  } else if (prop === 'humidityin') {
    return getWidgetTitle(theDevice, 'indoor') + ' Humidity';
  } else if (prop === 'feelsLikein') {
    return getWidgetTitle(theDevice, 'indoor') + ' Feels Like';
  } else if (prop === 'dewPointin') {
    return getWidgetTitle(theDevice, 'indoor') + ' Dew Point';
  } else if (prop === 'sunMoon') {
    return getWidgetTitle(theDevice, 'sunMoon');
  } else if (/relay/.test(prop)) {
    return getWidgetTitle(theDevice, prop);
  } else if (/^leak/.test(prop)) {
    return getWidgetTitle(theDevice, prop);
  } else if (/^batleak/.test(prop)) {
    return getWidgetTitle(theDevice, prop.replace('bat', '')) + ' Battery';
  } else if (/battr\d/.test(prop)) {
    return getWidgetTitle(theDevice, "relay".concat(prop.match(/\d+/)[0])) + ' Battery';
  } else if (/batt\d/.test(prop) && !/volts$/.test(prop)) {
    // exclude batt1volts and batt2volts
    var tempKey = "temp".concat(prop.match(/\d+/)[0], "f");
    var soilOrTemp = dataHas(theDevice.lastData, tempKey) ? tempKey : "soiltemp".concat(prop.match(/\d+/)[0]);
    return getWidgetTitle(theDevice, soilOrTemp) + ' Battery';
  } else if (/soilhum\d/.test(prop)) {
    return getWidgetTitle(theDevice, "soiltemp".concat(prop.match(/\d+/)[0])) + ' Moisture';
  } else if (/humidity\d/.test(prop)) {
    return getWidgetTitle(theDevice, "temp".concat(prop.match(/\d+/)[0], "f")) + ' Humidity';
  } else if (/feelsLike\d/.test(prop)) {
    return getWidgetTitle(theDevice, "temp".concat(prop.match(/\d+/)[0], "f")) + ' Feels Like';
  } else if (/dewPoint\d/.test(prop)) {
    return getWidgetTitle(theDevice, "temp".concat(prop.match(/\d+/)[0], "f")) + ' Dew Point';
  }

  if (ambient.DATA_SPEC[prop]) {
    return ambient.DATA_SPEC[prop].label;
  }

  if (prop === 'feelsLike') {
    return 'Feels Like';
  }

  if (prop === 'dewPoint') {
    return 'Dew Point';
  }

  if (prop === 'rain') {
    return 'Rainfall';
  }

  if (prop === 'rainTotal') {
    return 'Rain Total';
  }

  if (prop === 'notReporting') {
    return 'Device not reporting for 20 minutes';
  }
};

module.exports = {
  ambient: ambient
};